import React, { createContext, useState, ReactNode, useMemo } from "react";
import { RecaptchaTokens } from "@app/hooks";

export interface PublicMpStepperData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  mobileNumber: string;
  email: string;
  alternativeLastName?: string;
  zipCode?: string;
  middleName?: string;
}

export type PublicMpFailedErrorType =
  | "MP_ACTIVE_ALREADY_EXISTS"
  | "MP_NO_MATCHING_ORDER"
  | "MP_NO_PATIENT_MATCH"
  | "MP_NO_ORDER_FOUND"
  | "NO_SIGNED_ORDER"
  | "PROSPERA_EBDD_PASSED"
  | "MP_GENERIC_NO_MATCH_ERROR"; // Generic MP no-match/service/network error

export const publicMpMatchOrderErrorMapping: Record<
  string,
  PublicMpFailedErrorType
> = {
  MP_ACTIVE_ALREADY_EXISTS: "MP_ACTIVE_ALREADY_EXISTS",
  MP_NO_MATCHING_ORDER: "MP_NO_MATCHING_ORDER",
  MP_NO_PATIENT_MATCH: "MP_NO_PATIENT_MATCH",
  PROSPERA_EBDD_PASSED: "PROSPERA_EBDD_PASSED",
};

export interface PublicMpStepperDataController {
  publicMpStepperData?: PublicMpStepperData;
  savePublicMpStepperData: (data: PublicMpStepperData) => void;
  publicMpFailedErrorType?: PublicMpFailedErrorType;
  setPublicMpFailedErrorType: (errorType: PublicMpFailedErrorType) => void;
  publicMpToken?: string;
  setPublicMpToken: (token: string) => void;
  publicMpCode?: string;
  setPublicMpCode: (code: string) => void;
  recaptchaTokens: RecaptchaTokens | null;
  setRecaptchaTokens: (tokens: RecaptchaTokens | null) => void;
  returnToConfirmationForV2: boolean;
  setReturnToConfirmationForV2: (value: boolean) => void;
}

const defaultPublicMpStepperDataController: PublicMpStepperDataController = {
  publicMpStepperData: undefined,
  savePublicMpStepperData: () => undefined,
  publicMpFailedErrorType: undefined,
  setPublicMpFailedErrorType: () => undefined,
  publicMpToken: undefined,
  setPublicMpToken: () => undefined,
  publicMpCode: undefined,
  setPublicMpCode: () => undefined,
  recaptchaTokens: null,
  setRecaptchaTokens: () => undefined,
  returnToConfirmationForV2: false,
  setReturnToConfirmationForV2: () => undefined,
};

export const Context = createContext<PublicMpStepperDataController>(
  defaultPublicMpStepperDataController
);

Context.displayName = "PublicMpStepperDataContext";

interface PublicMpStepperDataProviderProps {
  children: ReactNode;
}

const PublicMpStepperDataProvider: React.FC<
  PublicMpStepperDataProviderProps
> = ({ children }) => {
  const [publicMpStepperData, setPublicMpStepperData] = useState<
    PublicMpStepperData | undefined
  >(undefined);

  const [publicMpFailedErrorType, setPublicMpFailedErrorType] =
    useState<PublicMpFailedErrorType>();

  const [publicMpToken, setPublicMpToken] = useState<string | undefined>();
  const [publicMpCode, setPublicMpCode] = useState<string | undefined>();
  const [recaptchaTokens, setRecaptchaTokens] =
    useState<RecaptchaTokens | null>(null);
  const [returnToConfirmationForV2, setReturnToConfirmationForV2] =
    useState(false);

  const savePublicMpStepperData = (data: PublicMpStepperData) => {
    setPublicMpStepperData(data);
  };

  const handlePublicMpFailedErrorType = (
    errorType: PublicMpFailedErrorType
  ) => {
    setPublicMpFailedErrorType(errorType);
  };

  const handleSetPublicMpToken = (token: string) => setPublicMpToken(token);
  const handleSetPublicMpCode = (code: string) => setPublicMpCode(code);

  const contextValue = useMemo(
    () => ({
      publicMpStepperData,
      savePublicMpStepperData,
      publicMpFailedErrorType,
      setPublicMpFailedErrorType: handlePublicMpFailedErrorType,
      publicMpToken,
      setPublicMpToken: handleSetPublicMpToken,
      publicMpCode,
      setPublicMpCode: handleSetPublicMpCode,
      recaptchaTokens,
      setRecaptchaTokens,
      returnToConfirmationForV2,
      setReturnToConfirmationForV2,
    }),
    [
      publicMpStepperData,
      publicMpFailedErrorType,
      publicMpToken,
      publicMpCode,
      recaptchaTokens,
      returnToConfirmationForV2,
    ]
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default PublicMpStepperDataProvider;
